import { assertNonNullable } from '@rauschma/helpers/typescript/type.js';
import type { ComponentChildren, VNode } from 'preact';
import { useContext } from 'preact/hooks';
import { PageInfoContext } from '../../client/core/page-info.js';

/** Fixed data (for the whole site) */
interface HtmlSkeletonProps {
  siteLanguage: string;
  favicon?: FaviconSpec;
  /** Data that changes per file */
  data: HtmlSkeletonData;
  children: ComponentChildren;
}

export const HYDRATION_DATA_ID = 'hydration-data';

export function HtmlSkeleton({siteLanguage, favicon, data, children}: HtmlSkeletonProps): VNode {
  const pageInfo = useContext(PageInfoContext);
  assertNonNullable(pageInfo);

  const peer = (absWebPath: string) => pageInfo.createPeerWebPath(absWebPath);

  let dynamic = null;
  if (data.codeBundleAbsWebPath && data.hydrationData) {
    dynamic = <>
      <script type="json" id={HYDRATION_DATA_ID} dangerouslySetInnerHTML={{ __html: JSON.stringify(data.hydrationData)}} />
      <script type="module" src={peer(data.codeBundleAbsWebPath)}></script>
    </>;
  }
  let faviconElements = null;
  if (favicon) {
    faviconElements = [
      <link rel="icon" type="image/svg+xml" href={peer(favicon.iconHref)} />,
      // @ts-expect-error
      <link rel="mask-icon" href={peer(favicon.maskIconHref)} color={favicon.maskIconColor} />,
      <link rel="apple-touch-icon" href={peer(favicon.appleTouchIconHref)} sizes="180x180" />,
    ];
  }
  
  return <html lang={siteLanguage}>
    <head>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width" />
      {faviconElements}
      <title>{data.pageTitlePlain}</title>
      {data.htmlHeadElements}
    </head>
    <body>
      {children}
    </body>
    {dynamic}
  </html>;
}

//==========

export interface HtmlSkeletonData {
  pageTitlePlain: string;
  htmlHeadElements: Array<VNode>;
  codeBundleAbsWebPath?: string;
  hydrationData?: unknown;
}

/**
 * @see doc/favicons.md
 */
export interface FaviconSpec {
  iconHref: string;
  maskIconHref: string;
  maskIconColor: string;
  appleTouchIconHref: string;
}
